import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/utils/axios";

import cls from "./ClubRoles.module.scss";

const ClubRoles = () => {
  const { cityId, streetId, clubId } = useParams();

  const backToClubPath = `/admin/cities/single/${cityId}/streets/${streetId}/clubs/${clubId}`;

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  const [partners, setPartners] = useState([]);
  const [hosts, setHosts] = useState([]);

  // search / assign
  const [q, setQ] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState(null);

  const [assigningRole, setAssigningRole] = useState(""); // "partner" | "host" | ""

  const base = useMemo(() => {
    return `/v1/admin/cities/${cityId}/streets/${streetId}/clubs/${clubId}`;
  }, [cityId, streetId, clubId]);

  const viewMembershipLink = (membershipId) =>
    `/admin/cities/single/${cityId}/streets/${streetId}/clubs/${clubId}/memberships/${membershipId}`;

  const fetchStaff = useCallback(async () => {
    if (!cityId || !streetId || !clubId) return;
    try {
      setLoading(true);
      setErr("");
      setMsg("");

      const { data } = await api.get(`${base}/roles/staff`);
      setPartners(Array.isArray(data?.partners) ? data.partners : []);
      setHosts(Array.isArray(data?.hosts) ? data.hosts : []);
    } catch (e) {
      setErr(
        e?.response?.data?.message || e?.message || "Failed to load staff."
      );
      setPartners([]);
      setHosts([]);
    } finally {
      setLoading(false);
    }
  }, [base, cityId, streetId, clubId]);

  useEffect(() => {
    fetchStaff();
  }, [fetchStaff]);

  const onSearch = async (e) => {
    e?.preventDefault?.();
    setErr("");
    setMsg("");
    setPicked(null);

    const term = String(q || "").trim();
    if (!term) {
      setResults([]);
      return;
    }

    try {
      setSearching(true);
      const { data } = await api.get(`${base}/roles/search-members`, {
        params: { q: term, limit: 20 },
      });
      setResults(Array.isArray(data?.members) ? data.members : []);
    } catch (e2) {
      setErr(e2?.response?.data?.message || e2?.message || "Search failed.");
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  const pickMember = (m) => {
    setPicked(m);
    setMsg("");
    setErr("");
  };

  const assign = async (role) => {
    if (!picked?.userId) {
      setErr("Select a member first.");
      return;
    }

    try {
      setAssigningRole(role);
      setErr("");
      setMsg("");

      await api.patch(`${base}/roles/assign`, {
        userId: picked.userId,
        role,
      });

      setMsg(`Assigned @${picked.username} as ${role}.`);
      setResults([]);
      setPicked(null);
      setQ("");

      await fetchStaff();
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || "Assign failed.");
    } finally {
      setAssigningRole("");
    }
  };

  const RoleList = ({ title, subtitle, items }) => {
    return (
      <section className={cls.card}>
        <div className={cls.cardHead}>
          <div>
            <h2 className={cls.cardTitle}>{title}</h2>
            <p className={cls.cardSub}>{subtitle}</p>
          </div>
          <span className={cls.countPill}>{items?.length || 0}</span>
        </div>

        {!items?.length ? (
          <div className={cls.empty}>No members yet.</div>
        ) : (
          <div className={cls.list}>
            {items.map((m) => {
              const avatarUrl = m?.avatar?.url || "";
              const username = m?.username || "unknown";
              const membershipStatus = m?.membershipStatus || "—";
              const userStatus = m?.userStatus || "—";
              const membershipId = m?.membershipId;

              return (
                <div key={m.membershipId} className={cls.row}>
                  <div className={cls.avatarWrap}>
                    {avatarUrl ? (
                      <img
                        className={cls.avatar}
                        src={avatarUrl}
                        alt={username}
                        loading="lazy"
                      />
                    ) : (
                      <div className={cls.avatarFallback} />
                    )}
                  </div>

                  <div className={cls.rowMain}>
                    <div className={cls.rowTop}>
                      <div className={cls.username}>@{username}</div>
                      <div className={cls.pills}>
                        <span className={cls.pill}>
                          club: {membershipStatus}
                        </span>
                        <span className={cls.pillMuted}>
                          user: {userStatus}
                        </span>
                      </div>
                    </div>

                    <div className={cls.rowMeta}>
                      <span className={cls.metaKey}>Role</span>
                      <span className={cls.metaVal}>
                        {m?.membershipRole || "—"}
                      </span>
                      <span className={cls.dot} />
                      <span className={cls.metaKey}>Member ID</span>
                      <span className={cls.metaVal}>{m?.memberId || "—"}</span>
                    </div>
                  </div>

                  <div className={cls.rowActions}>
                    {membershipId ? (
                      <Link
                        to={viewMembershipLink(membershipId)}
                        className={`nu-btn nu-btn--blue ${cls.viewBtn}`}
                      >
                        View
                      </Link>
                    ) : (
                      <button className={`nu-btn ${cls.viewBtn}`} disabled>
                        View
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    );
  };

  return (
    <div className={cls.page}>
      <header className={cls.header}>
        <div>
          <Link to={backToClubPath} className={cls.backLink}>
            ← Back to Club
          </Link>
          <p className={cls.eyebrow}>Admin · Club</p>
          <h1 className={cls.title}>Partners & Hosts</h1>
          <p className={cls.sub}>
            Manage staff roles for this club. Search a member and assign Partner
            or Host.
          </p>
        </div>

        <div className={cls.headerActions}>
          <button
            className={`nu-btn nu-btn--ghost ${cls.refreshBtn}`}
            onClick={fetchStaff}
            disabled={loading}
            type="button"
          >
            {loading ? "Loading…" : "Refresh"}
          </button>
        </div>
      </header>

      {err && <div className={cls.alertErr}>⚠ {err}</div>}
      {msg && <div className={cls.alertOk}>{msg}</div>}

      {/* Assign panel */}
      <section className={cls.assignCard}>
        <div className={cls.assignHead}>
          <h2 className={cls.cardTitle}>Add Partner / Add Host</h2>
          <p className={cls.cardSub}>
            Type a username. Pick a member from this club. Assign a role.
          </p>
        </div>

        <form className={cls.searchRow} onSubmit={onSearch}>
          <input
            className={cls.searchInput}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search username… (example: emily)"
          />

          <button
            className={`nu-btn nu-btn--pink ${cls.searchBtn}`}
            type="submit"
            disabled={searching || !q.trim()}
          >
            {searching ? "Searching…" : "Search"}
          </button>
        </form>

        <div className={cls.searchGrid}>
          {/* results */}
          <div className={cls.searchCol}>
            <div className={cls.searchTitle}>Results</div>

            {!results.length ? (
              <div className={cls.emptyMini}>
                {q.trim() ? "No matches." : "Type a username to search."}
              </div>
            ) : (
              <div className={cls.resultsList}>
                {results.map((m) => {
                  const isActive = picked?.membershipId === m.membershipId;
                  const avatarUrl = m?.avatar?.url || "";
                  const username = m?.username || "unknown";

                  return (
                    <button
                      key={m.membershipId}
                      type="button"
                      onClick={() => pickMember(m)}
                      className={`${cls.pickRow} ${
                        isActive ? cls.pickRowActive : ""
                      }`}
                    >
                      <div className={cls.pickAvatarWrap}>
                        {avatarUrl ? (
                          <img
                            className={cls.pickAvatar}
                            src={avatarUrl}
                            alt={username}
                            loading="lazy"
                          />
                        ) : (
                          <div className={cls.pickAvatarFallback} />
                        )}
                      </div>

                      <div className={cls.pickMain}>
                        <div className={cls.pickName}>@{username}</div>
                        <div className={cls.pickMeta}>
                          <span>role: {m.membershipRole}</span>
                          <span className={cls.sep}>•</span>
                          <span>club: {m.membershipStatus}</span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* selected */}
          <div className={cls.searchCol}>
            <div className={cls.searchTitle}>Selected</div>

            {!picked ? (
              <div className={cls.emptyMini}>Select a member from results.</div>
            ) : (
              <div className={cls.selectedCard}>
                <div className={cls.selectedTop}>
                  <div className={cls.selectedAvatarWrap}>
                    {picked?.avatar?.url ? (
                      <img
                        className={cls.selectedAvatar}
                        src={picked.avatar.url}
                        alt={picked.username}
                      />
                    ) : (
                      <div className={cls.selectedAvatarFallback} />
                    )}
                  </div>

                  <div className={cls.selectedInfo}>
                    <div className={cls.selectedName}>@{picked.username}</div>
                    <div className={cls.selectedMeta}>
                      <span className={cls.pill}>
                        current: {picked.membershipRole}
                      </span>
                      <span className={cls.pillMuted}>
                        club: {picked.membershipStatus}
                      </span>
                    </div>
                  </div>
                </div>

                <div className={cls.assignBtns}>
                  <button
                    type="button"
                    className={`nu-btn nu-btn--green ${cls.assignBtn}`}
                    onClick={() => assign("partner")}
                    disabled={assigningRole === "partner"}
                  >
                    {assigningRole === "partner"
                      ? "Assigning…"
                      : "Assign Partner"}
                  </button>

                  <button
                    type="button"
                    className={`nu-btn nu-btn--blue ${cls.assignBtn}`}
                    onClick={() => assign("host")}
                    disabled={assigningRole === "host"}
                  >
                    {assigningRole === "host" ? "Assigning…" : "Assign Host"}
                  </button>
                </div>

                {picked?.membershipId && (
                  <Link
                    className={`nu-btn nu-btn--ghost ${cls.viewSelectedBtn}`}
                    to={viewMembershipLink(picked.membershipId)}
                  >
                    View Membership
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Two columns: partners + hosts */}
      <div className={cls.grid}>
        <RoleList
          title="Partners"
          subtitle="People assigned to Partner role in this club."
          items={partners}
        />

        <RoleList
          title="Hosts"
          subtitle="People assigned to Host role in this club."
          items={hosts}
        />
      </div>
    </div>
  );
};

export default ClubRoles;
